import { useCallback, useMemo, useState } from 'react';
import { unique } from 'underscore';
import { useNavigate, useLocation, useParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getWorkingProject } from '@/store/slices/project/selectors';
import { getDefaultLanguage } from '@/utils/get-default-language';
import { TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import { saveWorkingProjectByFields, saveWorkingProjectSessionByField } from '@/store/slices/project';
import LanguagesSelector from '@ui/languages-selector/languages-selector';
import TranslateLangs from '@/utils/translate-langs.json';
import TranslateLangsMapV2 from '@/utils/translate-langs-map-v2.json';
import getAllProjectLanguages from '@/utils/get-all-project-languages';
import { LanguagesAbbr } from '@/types/translation.types';
import { SessionTypesEnum } from '@/types/session.types';
import replacePathnameLanguage from '@/utils/replace-pathname-language';
import type { I18NSettings } from '@/types/i18n.types';
import deepEqual from 'fast-deep-equal';

const TranslateLangsMap = TranslateLangs.reduce(
  (acc, lang) => {
    acc[lang.code] = lang;
    return acc;
  },
  {} as Record<string, { label: string; code: string }>
);

type TEditSessionLanguageProps = {
  sessionUuid: string | undefined;
};

const EditSessionLanguage = ({ sessionUuid }: TEditSessionLanguageProps) => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  const pathname = location.pathname;
  const paramsLanguage = params?.language;

  const project = useAppSelector(getWorkingProject);

  const sessions = useMemo(() => project?.sessions || [], [project?.sessions]);

  const session = sessions.find((s) => s.uuid === sessionUuid);

  const projectDefaultLanguage = getDefaultLanguage(project);

  const initialLanguage = session?.default_language || projectDefaultLanguage;

  const [defaultLanguage, setDefaultLanguage] = useState(initialLanguage);

  const isFiresideSession = session?.session_type === SessionTypesEnum.fireside;

  const defaultSelectedLanguages: () => TReactSelectOption[] = useCallback(() => {
    if (!session?.languages) {
      return [
        {
          value: defaultLanguage,
          label: TranslateLangsMap[defaultLanguage]?.label || defaultLanguage,
          meta: {
            value: defaultLanguage,
            label: TranslateLangsMap[defaultLanguage]?.label || defaultLanguage,
          },
        },
      ];
    }

    let hasDefaultLanguage = false;
    const selectedLanguages = session?.languages.map((lang) => {
      const match = TranslateLangsMap[lang];
      const value = match?.code || lang;
      const label = match?.label || lang;
      const isDefaultLanguage = lang === defaultLanguage;
      if (isDefaultLanguage) {
        hasDefaultLanguage = true;
      }
      return {
        value,
        label,
        meta: { value, label },
      };
    });

    if (!hasDefaultLanguage) {
      selectedLanguages.unshift({
        value: defaultLanguage,
        label: TranslateLangsMap[defaultLanguage]?.label || defaultLanguage,
        meta: {
          value: defaultLanguage,
          label: defaultLanguage,
        },
      });
    }
    return selectedLanguages;
  }, [session?.languages, defaultLanguage]);

  const handleSaveLanguages = useCallback(
    (selectedLanguages: TReactSelectOption[]) => {
      if (!project || !session || !sessionUuid) return;
      const _selectedLanguages = unique(selectedLanguages.map((lang) => lang.value.toString()));

      if (!_selectedLanguages.includes(defaultLanguage)) {
        _selectedLanguages.unshift(defaultLanguage);
      }

      const defaultLanguageCodes = defaultSelectedLanguages().map((lang) => lang.value.toString());

      let hasDiff = false;
      // if any item in selectedLanguagesSet is not in defaultSelectedLanguagesSet, then we have a diff, vice-versa
      if (initialLanguage !== defaultLanguage) {
        hasDiff = true;
      } else if (_selectedLanguages.length !== defaultLanguageCodes.length) {
        hasDiff = true;
      } else {
        hasDiff = !deepEqual(_selectedLanguages, defaultLanguageCodes);
      }

      /* firesides only allows one language, therefore the default must always match the user selected language */
      const newSessionDefaultLanguage = isFiresideSession ? _selectedLanguages[0] : defaultLanguage;

      const allProjectLanguages = getAllProjectLanguages(
        sessions.map((session) => {
          if (session.uuid === sessionUuid) {
            return {
              ...session,
              default_language: newSessionDefaultLanguage,
              languages: _selectedLanguages,
            };
          }
          return session;
        })
      ) as LanguagesAbbr[];

      const newSettings = { ...project.settings };

      // enforce that i18n is set
      if (!newSettings.i18n) {
        newSettings.i18n = {
          base: defaultLanguage ?? 'en',
          event_languages: allProjectLanguages,
        };
      }

      // if there's any difference between the last and current event langauge, there is a diff
      if (new Set(allProjectLanguages).difference(new Set((newSettings.i18n as I18NSettings).event_languages)).size) {
        hasDiff = true;
      }

      // if the base language has changed, there is a diff
      if ((newSettings.i18n as I18NSettings).base !== defaultLanguage) {
        hasDiff = true;
      }

      if (!hasDiff) return;

      // update the event_languages in the project settings
      dispatch(
        saveWorkingProjectByFields({
          settings: {
            ...newSettings,
            i18n: {
              base: defaultLanguage ?? (newSettings.i18n as I18NSettings).base,
              event_languages: allProjectLanguages,
            },
          },
        })
      );
      // update the session with the new updatedSelectedLanguages languages
      dispatch(
        saveWorkingProjectSessionByField({
          languages: _selectedLanguages,
          uuid: sessionUuid,
          default_language: newSessionDefaultLanguage,
          translate_all: true, // tells the API to translate modules
        })
      );

      // we need to navigate to the new default language
      if (paramsLanguage && newSessionDefaultLanguage && paramsLanguage !== newSessionDefaultLanguage) {
        const newPath = replacePathnameLanguage({
          pathname,
          paramsLanguage,
          updatedLanguage: newSessionDefaultLanguage,
        });
        navigate(newPath);
      }
    },
    [
      defaultLanguage,
      defaultSelectedLanguages,
      dispatch,
      initialLanguage,
      isFiresideSession,
      pathname,
      navigate,
      paramsLanguage,
      project,
      session,
      sessionUuid,
      sessions,
    ]
  );

  // get any non-v2 languages, so we can add them to the list of languages for the language selector
  // to maintain backward compatibility for older projects
  const nonV2Languages = useMemo(() => {
    return session?.languages?.filter((lang) => {
      const translateMap = TranslateLangsMapV2 as Record<string, string>;
      const v2Lang = translateMap[lang];
      if (!v2Lang) {
        return true;
      }

      return false;
    });
  }, [session?.languages]);

  if (!session) return null;
  return (
    <LanguagesSelector
      key={session?.session_type}
      defaultLanguage={defaultLanguage}
      defaultSelectedLanguages={defaultSelectedLanguages()}
      onMenuClose={handleSaveLanguages}
      nonV2Languages={nonV2Languages}
      onBaseSelect={(data) => setDefaultLanguage(data.value.toString())}
      isMulti={!isFiresideSession}
      hideBaseSelector={isFiresideSession}
    />
  );
};

export default EditSessionLanguage;
